import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Button,
  SelectChangeEvent,
  FormLabel,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../utils/tokenSetCookie";
import LoginRestriction from "../../utils/loginRestriction";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import ExpandableTable from "./usageReportsTable";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface LocationClass {
  id: number;
  businessEntityId: number;
  className: string;
  deletedOn: string | null; // Assuming it's a date or null
}
interface Location {
  accountNumber: string | null;
  businessCity: string | null;
  businessEntityId: number;
  businessEntityLocationType: number;
  businessPhone: string | null;
  businessState: string | null;
  businessStreet1: string | null;
  businessUnit: string | null;
  businessZip: string | null;
  businessentityLocationTypeName: string;
  classId: string; // Assuming this is a comma-separated string like "1,2,3"
  className: string; // Assuming this is a comma-separated string like "Test 2, Test 3, Test1"
  countryCode: string | null;
  createdOn: string | null; // If it's a date, use `Date | null`
  deletedOn: string | null; // If it's a date, use `Date | null`
  id: number;
  locationBrand: string | null;
  locationName: string;
  storeNumber: string | null;
  webSiteId: number;
}

const UsageReport: React.FC = () => {
  const { t } = useTranslation();
  const [locationClass, setLocationClass] = useState<LocationClass[]>([]);
  const [location, setLocation] = useState<Location[]>([]);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);
  
  // Mock data
  const initialData = {
    organizationName: "Clinical Visuals",
    users: [
      { id: "all", userName: "All" },
      { id: "admin", userName: "Admin" },
      { id: "editor", userName: "Editor" },
      { id: "viewer", userName: "Viewer" },
    ],
    dateFilter: ["daily", "weekly", "monthly"],
    reportTypes: ["Website"],
  };

  const [formData, setFormData] = useState({
    businessEntity: initialData.organizationName,
    classId: "All",
    location: "All",
    user: "All",
    dateFilter: "daily",
    // report_type: "Website",
    reportFilter: "Website",
    reportTypes: ["Website"],
  });
  const [reportData, setReportData] = useState<any>(null);
  // const [userBussinessEntity, setuserBussinessEntity] = useState();
  const [userList, setUserList] = useState(initialData.users); // Store users in state
  const [userBussinessEntity, setuserBussinessEntity] = useState(Number);
  useEffect(() => {
    fetchVideoData();
    GetuserDetails();
  }, []);

  useEffect(() => {
    GetuserDetailsBasedOnSelect("All");
    GetuserDetailsBasedOnClass("All");
    getLocationListData();
  }, [userBussinessEntity]);

  const getLocationListData = async () => {
    try {
      const response = await axiosInstance.get(API_BASE_URL + "location/list/");
      if (response.status >= 200 && response.status <= 300) {
        setLocation(response.data.data); // Save data to state
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  const GetuserDetailsBasedOnSelect = async (value: string) => {
    try {
      let params = {
        businessEntityId: userBussinessEntity,
        locationId: value,
        isDeleted: 0,
        userName: "",
        classId: "",
        accountAccessType: "",
      };

      let response_userDetails = await axiosInstance.post(
        API_BASE_URL + "users/manage-user",
        params
      );
      if (
        response_userDetails.status >= 200 &&
        response_userDetails.status <= 300
      ) {
        let userData = response_userDetails.data.data;
        if (userData.length > 0) {
          setUserList(userData); // Merge stati
        } else {
          setUserList([]);
        }
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  const GetuserDetailsBasedOnClass = async (value: string) => {
    localStorage.setItem("classIds", value);
    try {
      value
      let params = {
        businessEntityId: userBussinessEntity,
        classId: value,
      };
      let response_locationDetails = await axiosInstance.get(
        API_BASE_URL + "location/locationByClass",
        {
          params: params, // Pass params inside an object
        }
      );
      if (
        response_locationDetails.status >= 200 &&
        response_locationDetails.status < 300
      ) {
        let locationDetails = response_locationDetails.data.data;
        setLocation(locationDetails);
        setFormData((prevData) => ({
          ...prevData,
          location: "All",
          user: "All",
        }));
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  const handleInputChange = (
    event:
      | SelectChangeEvent<string>
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name == "location") {
      GetuserDetailsBasedOnSelect(value);
    }

    if (name == "classId") {
      GetuserDetailsBasedOnClass(value);
    }
  };

  const fetchVideoData = async () => {
    try {
      const results = await Promise.allSettled([
        axiosInstance.get(API_BASE_URL + "locationclass/list"),
      ]);
      // Handle locationClass API response
      if (results[0].status === "fulfilled") {
        setLocationClass(results[0].value.data.data);
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  const GetuserDetails = async () => {
    try {
      let response_userDetails = await axiosInstance.get(
        API_BASE_URL + "users/getuserdetails"
      );
      if (
        response_userDetails.status >= 200 &&
        response_userDetails.status <= 300
      ) {
        setuserBussinessEntity(
          response_userDetails.data.data.userBusinessEntityId
        );
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  //for getting user report
  const handleGenerateReport = async () => {
    try {
      const params = new URLSearchParams({
        businessentityId: "1",
        classId: formData.classId?.toString() || "", // Convert to string
        locationId: formData.location?.toString() || "", // Convert to string
        dateFilterType: formData.dateFilter?.toString() || "", // Convert to string
        userId: formData.user?.toString() || "", // Convert to string
        reportFilter: formData.reportFilter?.toString() || "", // Convert to string
      });
      // Make GET request with URL parameters
      const response = await axiosInstance.get(
        `${API_BASE_URL}animation-viewed/usagereports?${params.toString()}`
      );
      // Handle successful response
      if (response.status >= 200 && response.status < 300) {
        setReportData({
          formattedTitle: response.data.data.formattedTitle,
          class: response.data.data.formattedData.class,
          noClass: response.data.data.formattedData.noClass,
        });
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };

  const handleGenerateRawReport = async () => {
    try {
      const params = new URLSearchParams({
        businessentityId: "1",
        classId: formData.classId?.toString() || "", // Convert to string
        locationId: formData.location?.toString() || "", // Convert to string
        dateFilterType: formData.dateFilter?.toString() || "", // Convert to string
      });
      // Set responseType to "blob" to receive binary data
      const response = await axiosInstance.get(
        `${API_BASE_URL}animation-viewed/getrawdata?${params.toString()}`,
        {
          responseType: "blob", //  FIX: Axios will return response.data as a Blob
        }
      );
      if (response.status >= 200 && response.status < 300) {
        const blob = response.data; //  FIX: Access blob directly from response.data
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url); // Clean up
      } else {
      }
    } catch (error) {
      const errCode = error as AxiosError<{ status: number }>;
      if (errCode.response && errCode.response.status === 401) {
        setIsRestrictionOpen(true);
      } else {
        toast.error("Something went wrong", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      }
      throw error;
    }
  };
  const handletypeChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box>
      <Box
        sx={{
          border: 1,
          borderColor: "grey.400",
          margin: {
            xs: "10px 55px 20px 10px",
            sm: "0px 60px 10px 30px",
            md: "0px 50px 10px 50px",
            lg: "0px 70px 20px 50px",
          },
          borderRadius: 2,
          paddingTop: 2,
        }}
      >
        <Typography
          variant="h5"
          className="headingtitle"
          sx={{
            borderBottom: "1px solid #ccc",
            paddingBottom: 1,
            ml: 2,
            mt: 1,
          }}
        >
          {t("usage_report")}
        </Typography>

        <form>
          <Grid container spacing={3} sx={{ padding: 2 }}>
            <Grid item xs={12} md={3}>
              <FormLabel className="formlabel">
                {t("organization_name")}
              </FormLabel>
              <TextField
                value={formData.businessEntity}
                size="small"
                variant="outlined"
                fullWidth
                sx={{ backgroundColor: "#f0f2f4" }}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel className="formlabel">
                  {t("location_class")}
                </FormLabel>

                <Select
                  name="classId"
                  value={formData.classId}
                  onChange={handleInputChange}
                  size="small"
                >
                  <MenuItem value="All">All</MenuItem>
                  {locationClass.map((location, index) => (
                    <MenuItem key={index} value={location?.id || ""}>
                      {location.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel className="formlabel">{t("location")}</FormLabel>
                <Select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  size="small"
                >
                  <MenuItem value="All">All</MenuItem>
                  {location?.map((locations, index) => (
                    <MenuItem key={index} value={locations?.id || ""}>
                      {locations.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel className="formlabel">{t("user")}</FormLabel>

                <Select
                  name="user"
                  value={formData.user}
                  onChange={handleInputChange}
                  size="small"
                >
                  <MenuItem value="All">All</MenuItem>
                  {userList?.map((user) => (
                    <MenuItem key={user?.id} value={user?.id}>
                      {user?.userName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel className="formlabel">{t("date_filter")}</FormLabel>

                <Select
                  name="dateFilter"
                  value={formData.dateFilter}
                  onChange={handleInputChange}
                  size="small"
                >
                  {initialData.dateFilter.map((dateFilter) => (
                    <MenuItem key={dateFilter} value={dateFilter}>
                      {dateFilter?.charAt(0).toUpperCase() +
                        dateFilter?.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel className="formlabel">{t("Report Type")}</FormLabel>

                <Select
                  name="reportFilter" // ✅ corrected to match formData key
                  value={formData.reportFilter} // ✅ must match formData field
                  onChange={handletypeChange}
                  size="small"
                >
                  {initialData.reportTypes.map((reportType) => (
                    <MenuItem key={reportType} value={reportType}>
                      {reportType?.charAt(0).toUpperCase() + reportType?.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>



            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: "none" }}
                  startIcon={<Search />}
                  onClick={handleGenerateReport} // Function executes on button click
                >
                  {t("generate_report")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    textTransform: "none",
                  }}
                  startIcon={<Search />}
                  onClick={handleGenerateRawReport} // Function executes on button click
                >
                  {t("generate_usage_raw_report")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

      {reportData && (
        <Box
          sx={{
            border: 1,
            borderColor: "grey.400",
            margin: {
              xs: "10px 55px 20px 10px",
              sm: "20px 60px 10px 30px",
              md: "10px 50px 10px 50px",
              lg: "20px 70px 20px 50px",
            },
            borderRadius: 2,
            padding: 2,
          }}
        >
          <ExpandableTable data={reportData}
            dateFilter={formData.dateFilter}
            businessEntity={userBussinessEntity.toString()}
            reportFilter={formData.reportFilter}
          ></ExpandableTable>
        </Box>
      )}
      <LoginRestriction open={isRestrictionOpen} />
    </Box>
  );
};

export default UsageReport;